import { createFileRoute } from "@tanstack/react-router";
import { SiteShell } from "@/components/site/SiteShell";
import { PillarArticle, articleJsonLdForPillar } from "@/components/site/PillarArticle";
import { buildHead, breadcrumbJsonLd, faqJsonLd } from "@/lib/seo";
import cover from "@/assets/cover-pillar-conversion-es.jpg";
import cover640 from "@/assets/cover-pillar-conversion-es-640.webp";
import cover960 from "@/assets/cover-pillar-conversion-es-960.webp";
import cover1280 from "@/assets/cover-pillar-conversion-es-1280.webp";
import { setEdgeHtmlCache } from "@/lib/cache-headers";

const COVER_SRCSET = `${cover640} 640w, ${cover960} 960w, ${cover1280} 1280w`;
const COVER_SIZES = "(min-width: 768px) 720px, calc(100vw - 48px)";

const PATH = "/es/recursos/playbook-cro-shopify";
const ALT = "/resources/shopify-conversion-rate-optimization-playbook";
const TITLE = "Playbook de CRO en Shopify: encuentra y corrige fugas de conversión";
const DESC =
  "Un marco práctico de CRO en Shopify: valida el tracking, segmenta el funnel, corrige primero las fugas técnicas y de UX, y testea solo donde hay alternativas reales.";
const PUBLISHED = "2026-05-19T09:00:00.000Z";
const MODIFIED = "2026-10-28T09:00:00.000Z";

const SECTIONS = [
  {
    id: "por-que-el-consejo-falla",
    heading: "Por qué la mayoría de consejos de CRO Shopify son malos para tiendas de $1M+",
    blocks: [
      "La mayoría del contenido de CRO en internet está escrito para tiendas que facturan $30k/mes, donde cualquier cambio de color de botón mueve la aguja porque el sample size es minúsculo. Pasando $1M ARR la forma de la palanca es completamente distinta. Los tweaks chiquitos no mueven el medidor porque tu piso ya es alto. Lo que mueve el medidor es estructural, arquitectura de página, lógica de merchandising, flujo de cart y checkout, y señales de confianza.",
      "Este playbook es el sistema operativo de CRO que corremos en tiendas Shopify Plus de $1M–$50M. Asume que ya tienes analytics funcional, tráfico real y una marca que alguien ha escuchado. Si estás antes de eso, manda más producto primero y vuelve.",
    ],
  },
  {
    id: "sistema-cro",
    heading: "Sistema operativo de CRO: investigar → hipotetizar → testear → enviar",
    blocks: [
      "Tests random sobre páginas random se cancelan entre sí. Un programa real de CRO corre sobre un loop de cuatro pasos, cada semana, para siempre.",
      "### 1. Investigar",
      "Sacar analytics de funnel, session recordings (Hotjar, Microsoft Clarity), queries de búsqueda, tickets de soporte, razones de devolución y data de surveys on-site. El output es una lista priorizada de fricciones, no opiniones.",
      "### 2. Hipotetizar",
      "Cada hipótesis es una sola oración: \"Si [cambiamos X] en [página Y], [métrica Z] mejorará [N%] porque [evidencia A].\" Cualquier cosa más difusa es un deseo, no hipótesis.",
      "### 3. Testear",
      "Testea donde tienes tráfico para aprender. Cuántos tests puedes correr al mismo tiempo depende del tamaño de muestra que requiere cada uno, que sale de tu tasa de conversión base, el efecto mínimo que necesitas detectar y el tráfico que recibe cada test, no solo de la facturación. Calcúlalo antes de comprometerte y protege los tests de mayor palanca.",
      "### 4. Enviar",
      "Los ganadores van a 100%. Los perdedores se documentan en tu log de aprendizajes, ese log es el asset más valioso que construye el programa, más que cualquier test individual.",
    ],
  },
  {
    id: "pdp",
    heading: "PDP: la página de mayor palanca de tu tienda",
    blocks: [
      "Si tienes una hora para CRO este mes, gástala en PDPs. Tu PDP es donde los compradores deciden si vales el click, y donde la tienda promedio pierde 15–25% de add-to-cart potencial.",
      "### Sobre el fold",
      "- Título de producto con el job-to-be-done real, no solo el nombre del SKU.",
      "- Hero a tamaño correcto con `fetchpriority` y srcset, PDPs lentos pierden más que PDPs feos.",
      "- Precio con contexto promocional (anchor, member price, savings stack).",
      "- Selector de variantes que no se resetea con combinaciones out-of-stock.",
      "- Add-to-cart con label real (\"Agregar, $89\"), no un \"AGREGAR\" genérico.",
      "### Bajo el fold",
      "Reviews con fotos, tabla comparativa, guía de tallas/fit, política de devoluciones y el FAQ que responde las preguntas que soporte recibe 50× por semana. Hazlos escaneables, los compradores no leen, escanean.",
    ],
  },
  {
    id: "cart-checkout",
    heading: "Cart y checkout: donde el dinero se fuga en silencio",
    blocks: [
      "El abandono de cart y checkout casi nunca es por un solo problema grande. Son una docena de chicos que componen. La cura es auditar y quitar fricción, no agregar más módulos de upsell.",
      "### Cart drawer",
      "- Mostrar line items, claridad de precio y cualquier lógica de descuento transparentemente.",
      "- Un upsell, no tres. Más upsells distraen del cierre del cart.",
      "- Shipping estimado cuando se pueda, sorpresa de shipping en checkout es la #1 razón de abandono.",
      "### Checkout",
      "- Usar checkout extensibility de Shopify. Migrar de `checkout.liquid` si no lo has hecho.",
      "- Reducir campos. Cada campo opcional que no es opcional es un costo.",
      "- Honrar Shop Pay vaulting, es el método de pago de mayor conversión por 20–30%.",
      "- Trust signals en el step de pago (return policy, security, contacto de soporte).",
    ],
  },
  {
    id: "search-merch",
    heading: "Search, navegación y merchandising",
    blocks: [
      "Search convierte 3–5× más que browse. Si tu search está roto (sin resultados, resultados malos, sin sinónimos), estás pagando tráfico que podía haberse cerrado solo.",
      "### Search",
      "Usar Shopify Search & Discovery o Algolia. Configurar sinónimos para tus top 100 misspellings. Mandar queries \"sin resultados\" a tu review semanal de merchandising.",
      "### Navegación",
      "Mega menu para tiendas con 100+ SKUs. Agrupar por job-to-be-done, no por taxonomía interna. Testear menú mobile aparte, tiene ergonomía distinta.",
      "### Merchandising",
      "El orden del PLP importa más de lo que se cree. Default debería ser \"likely to convert\", no \"newest\". Pinear héroes. Degradar out-of-stock. Re-rankear semanal según inventory y margen.",
    ],
  },
  {
    id: "mobile",
    heading: "Mobile no es desktop con márgenes más chicos",
    blocks: [
      "La mayoría del tráfico DTC es mobile. La mayoría de testing de CRO se hace en desktop. Ese gap es por qué la conversión mobile se estanca.",
      "### Auditoría mobile-first",
      "- Testear cada flujo en un teléfono real mid-tier con network throttled, no en emulador de desktop.",
      "- Sticky add-to-cart en scroll de PDP es uno de los patrones mobile de mayor palanca.",
      "- Thumb zones, todo lo importante dentro del 60% inferior de la pantalla.",
      "- Reducir campos de form agresivamente. Shop Pay, Apple Pay, Google Pay como primarios.",
      "### Métricas mobile-específicas",
      "Trackear mobile y desktop por separado. Sus funnels se comportan distinto y las mejoras en uno no se transfieren al otro automáticamente.",
    ],
  },
  {
    id: "metricas",
    heading: "Las métricas que importan: revenue per session, AOV, repeat rate",
    blocks: [
      "La tasa de conversión aislada es métrica de vanidad. Las métricas contra las que corremos programas:",
      "### Revenue per session (RPS)",
      "Métrica maestra. Captura conversión, AOV y disciplina de descuento en un solo número. Si RPS sube, el programa está funcionando.",
      "### Average order value (AOV)",
      "Se mueve con bundles, free shipping threshold y upsell en el momento correcto (cart, no PDP). Testear descuentos contra AOV, no solo contra conversión.",
      "### Repeat rate / LTV",
      "El crecimiento de la mayoría de tiendas está bloqueado por retención, no adquisición. Post-purchase, flows de email/SMS, ofertas de suscripción y la experiencia de los primeros 30 días viven aquí.",
      "### CPA pagado",
      "Si el CRO está funcionando, tu CPA pagado baja. Programas de CRO que no mueven CPA no están moviendo lo correcto.",
    ],
  },
  {
    id: "tests-sin-trafico",
    heading: "Correr tests cuando no tienes tráfico de Optimizely",
    blocks: [
      "La mayoría de tiendas Shopify no tienen el tráfico para correr tests A/B clásicos a 95% de confianza en dos semanas. Pretender que sí lleva a falsos ganadores y falsos perdedores.",
      "### Frameworks pragmáticos",
      "- Sequential testing con inferencia Bayesiana (GrowthBook, Statsig) es más honesto a bajo tráfico.",
      "- Análisis pre/post con holdouts apropiados funciona para cambios estructurales irreversibles (nuevo template de PDP, nuevo checkout).",
      "- Multi-armed bandits para decisiones tipo merchandising donde quieres mover tráfico al ganador en medio del test.",
      "### Cuándo saltarte el test",
      "Si un cambio es cualitativamente mejor obvio (página más rápida, copy más claro, feature roto arreglado), envíalo. No quemes semanas A/B testeando un fix que ibas a enviar de todos modos.",
    ],
  },
];

const FAQS = [
  {
    q: "¿En cuánto se ve el trabajo de CRO en ingresos?",
    a: "Las victorias estructurales (rebuild de PDP, migración de checkout, redesign de cart) suelen mover RPS en 14–30 días. Los programas de tests toman 60–90 días en componer, el valor está en la cadencia de aprendizaje, no en un test individual.",
  },
  {
    q: "¿Necesitamos una herramienta de A/B testing?",
    a: "Eventualmente sí, GrowthBook o Statsig son nuestros defaults en Shopify. Pero los primeros 90 días de un programa de CRO suelen ser fixes estructurales que no necesitan herramienta. Súmala cuando tengas el tráfico y la cadencia para usarla.",
  },
  {
    q: "¿En qué tasa de conversión debo estar?",
    a: "Tiendas Shopify Plus medianas de $1M–$50M ARR están en 2.0–3.2% mobile y 3.2–4.5% desktop. Si estás bajo la mediana de tu categoría, CRO estructural tiene espacio. Arriba de la mediana los programas se vuelven incrementales.",
  },
  {
    q: "¿Me enfoco en conversión o en AOV?",
    a: "Trackea RPS. Captura ambas. Si conversión está bajo benchmark, prioriza quitar fricción. Si AOV está bajo benchmark, prioriza bundles, free shipping thresholds y cart upsells.",
  },
  {
    q: "¿CRO puede reemplazar adquisición pagada?",
    a: "No, pero hace que cada dólar de pagado rinda más. Un lift de 15% en RPS sobre una tienda de $5M financia otro 15% de tráfico pago al mismo ROAS combinado.",
  },
];

const RELATED = [
  { label: "Ver todas las guías de conversión →", href: "/es/recursos/conversion" },
  { label: "Servicio: CRO & SEO Cleanup", href: "/es/cro-shopify" },
  { label: "Pilar: Agencia Shopify Plus", href: "/es/agencia-shopify-plus" },
];

export const Route = createFileRoute("/es/recursos/playbook-cro-shopify")({
  loader: async () => { await setEdgeHtmlCache(); },
  head: () =>
    buildHead({
      locale: "es",
      title: TITLE,
      description: DESC,
      path: PATH,
      altPath: ALT,
      ogImage: cover,
      type: "article",
      author: "Samuel Noriega",
      articleAuthor: "Samuel Noriega",
      articleSection: "Conversión",
      publishedTime: PUBLISHED,
      modifiedTime: MODIFIED,
      jsonLd: [
        articleJsonLdForPillar({ image: cover,
          title: TITLE,
          description: DESC,
          path: PATH,
          publishedTime: PUBLISHED,
          modifiedTime: MODIFIED,
          section: "Conversión",
        }),
        breadcrumbJsonLd([
          { name: "Inicio", path: "/es" },
          { name: "Recursos", path: "/es/recursos" },
          { name: "Conversión", path: "/es/recursos/conversion" },
          { name: "Playbook de CRO", path: PATH },
        ]),
        faqJsonLd(FAQS),
      ],
    }),
  component: () => (
    <SiteShell>
      <PillarArticle
        locale="es"
        path={PATH}
        eyebrow="Conversión"
        title="Playbook de CRO Shopify para marcas DTC de $1M+"
        intro="El sistema operativo de CRO que corremos en tiendas Shopify Plus: cómo investigamos, hipotetizamos, testeamos y enviamos, y los templates que usamos en PDP, cart, checkout y merchandising."
        updated="Mayo 2026"
        readingMinutes={19}
        hubHref="/es/recursos/conversion"
        hubLabel="Ver todas las guías de conversión"
        sections={SECTIONS}
        cover={{ src: cover, srcSet: COVER_SRCSET, sizes: COVER_SIZES, alt: "Playbook de CRO para Shopify, portada ilustrada de cebra" }}
        midCTA={{
          eyebrow: "Pulse para CRO",
          title: "Arranca con un diagnóstico de conversión de 5 días",
          body: "Un estratega senior audita tu funnel, PDP, cart y checkout contra benchmarks de 100+ tiendas y te manda lista priorizada de fixes con estimaciones de revenue.",
          href: "/es/pulse",
          label: "Ver Pulse",
        }}
        finalCTA={{
          eyebrow: "Listo para arrancar",
          title: "Cotiza un proyecto de CRO en 48 horas",
          body: "Cuéntanos tu tienda, tu RPS actual y el step de funnel que está fugando. Te mandamos plan de alcance fijo con timeline, entregables y precio.",
          href: "/es/cotizacion?source=pillar-conversion",
          label: "Cotizar proyecto",
        }}
        faqs={FAQS}
        related={RELATED}
        publishedTime={PUBLISHED}
        modifiedTime={MODIFIED}
      />
    </SiteShell>
  ),
});